import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    addPhoto,
    getActiveSession,
    getSessionPhotosForTarget,
    removePhoto,
} from "../offline/sessionManager";
import { callAvosApi } from "../utils/useAvosApiHook";

export interface CheckImage {
    image_id: number;
    type: string;
    url?: string;
    url_webp?: string;
    base64?: string;
    focusMode?: any;
    [key: string]: any;
}

// Hook to get check images
export const useCheckImages = (tenantId: string | undefined, checkId: number | undefined) => {
    return useQuery({
        // networkMode: "always" so the queryFn runs while offline. With the
        // default "online", TanStack pauses the query when navigator.onLine is
        // false and never calls queryFn — so the IndexedDB read below is
        // skipped, the captured photo never reaches the form/modal, and no
        // preview renders. It also lets invalidateQueries() after a capture
        // actually refetch. The matching mutation already uses "always".
        networkMode: "always",
        queryKey: ["checkImages", tenantId, checkId],
        queryFn: async (): Promise<CheckImage[]> => {
            // Offline path: when a session is active, the synthetic
            // check_id is a temp id that won't resolve server-side.
            // Serve photos from the session instead.
            //
            // `getActiveSession()` (not `getActiveSessionId()`) because the
            // pointer outlives the row once replay deletes it — a dead id
            // would make us read an empty session and return [] forever
            // instead of falling through to the server.
            const session = await getActiveSession();
            if (session && checkId) {
                return getSessionPhotosForTarget(session.id, "check", checkId);
            }
            const { data } = await callAvosApi(`/${tenantId}/images/check/${checkId}/`);
            return data || [];
        },
        enabled: !!tenantId && !!checkId && checkId > 0,
    });
};

// Hook to add a check image
export const useAddCheckImage = () => {
    const queryClient = useQueryClient();

    return useMutation({
        // networkMode: "always" lets the offline-session branch below run
        // while offline. Default "online" would pause the mutation until
        // reconnection, bypassing the session-write branch entirely.
        networkMode: "always",
        mutationFn: async ({ tenantId, checkId, type, base64, focusMode }: {
            tenantId: string;
            checkId: number;
            type: string;
            base64: string;
            focusMode: any;
        }) => {
            // See useCheckImages: a stale pointer would send addPhoto at a
            // deleted session, which throws and surfaces as "Failed to save
            // Image" even though we're online and the server would accept it.
            const session = await getActiveSession();
            if (session) {
                const { syntheticImage } = await addPhoto(session.id, {
                    type,
                    target: "check",
                    target_temp_id: checkId,
                    base64,
                    focusMode,
                });
                return syntheticImage;
            }
            const { data } = await callAvosApi(`/${tenantId}/images/check/${checkId}/add_image/`, {
                method: "POST",
                data: { check_id: checkId, type, base64, focusMode }
            });
            return data;
        },
        onSuccess: (data, variables) => {
            // Invalidate and refetch check images after adding
            queryClient.invalidateQueries({ queryKey: ["checkImages", variables.tenantId, variables.checkId] });
            queryClient.invalidateQueries({ queryKey: ["check", variables.tenantId, variables.checkId] });
        },
    });
};

// Hook to delete a check image
export const useDeleteCheckImage = () => {
    const queryClient = useQueryClient();

    return useMutation({
        // See useAddCheckImage for the networkMode: "always" rationale.
        networkMode: "always",
        mutationFn: async ({ tenantId, checkId, image }: {
            tenantId: string;
            checkId: number;
            image: CheckImage;
        }) => {
            const session = await getActiveSession();
            if (session) {
                const photos = await getSessionPhotosForTarget(session.id, "check", checkId);
                const idx = photos.findIndex((p) => p.image_id === image.image_id);
                if (idx >= 0) {
                    await removePhoto(session.id, "check", checkId, idx);
                }
                return { ok: true };
            }
            const { data } = await callAvosApi(`/${tenantId}/images/check/${checkId}/delete_image/`, {
                method: "DELETE",
                data: image
            });
            return data;
        },
        onSuccess: (data, variables) => {
            // Invalidate and refetch check images after deleting
            queryClient.invalidateQueries({ queryKey: ["checkImages", variables.tenantId, variables.checkId] });
            queryClient.invalidateQueries({ queryKey: ["check", variables.tenantId, variables.checkId] });
        },
    });
};

